import { Loading3QuartersOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import HomepageEditPageModal from '@/components/Homepage/HomepageEditPageModal';
import Icon from '@/components/Icon';
import CheckSvg from '@/components/Icon/svgs/check_circle.svg';
import FailedSvg from '@/components/Icon/svgs/fill-warning.svg';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import { useModel } from '@/hooks/useModel';
import useStorage from '@/hooks/useStorage';

import styles from './index.less';

interface IProps {
  open: boolean;
  onCancel: () => void;
}

const AppearanceModal = (props: IProps) => {
  const { open, onCancel } = props;
  const { t } = useTranslation();

  const [editOpen, setEditOpen] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [layoutSize] = useStorage('layoutSize');
  const inMobile = useMemo(() => {
    return layoutSize === LAYOUT_SIZE.SMALL;
  }, [layoutSize]);

  const { userProfile, loadUserDetail } = useModel('auth-model', (model) => ({
    userProfile: model.userProfile,
    loadUserDetail: model.loadUserDetail
  }));

  const avatar = useMemo(
    () => userProfile?.homepage?.cover || userProfile?.avatar || '',
    [userProfile]
  );

  const isLoading = useMemo(() => {
    return !!(
      !!userProfile?.homepage?.cover &&
      userProfile.homepage.isQualifiedCover !== false &&
      userProfile.homepage.isQualifiedCover !== true
    );
  }, [userProfile]);

  useEffect(() => {
    if (isLoading) {
      timerRef.current = setInterval(() => {
        loadUserDetail();
      }, 3000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isLoading]);

  return (
    <Modal
      centered
      className={classNames(styles.appearanceModal, { [styles.inMobile]: inMobile })}
      closable={false}
      destroyOnClose
      footer={null}
      onCancel={() => {
        onCancel();
      }}
      open={open}
    >
      <div className="flex flex-col items-center gap-8">
        <div className="text-center text-[18px] leading-8 font-weight-500">
          {t('appearance.appearance')}
        </div>
        <div
          className="w-[140px] h-[140px] mx-auto rounded-[50%] border-[3px] border-solid border-[#FEFDFF] box-shadow-[0px_8px_32px_0px_rgba(194,158,107,0.15)] cursor-pointer hover:opacity-80 transition-opacity relative"
          onClick={() => {
            setEditOpen(true);
          }}
        >
          <img alt="" className="w-full h-full rounded-[50%] object-cover" src={avatar} />
          {isLoading ? (
            <Loading3QuartersOutlined className="absolute right-0 bottom-0 text-[32px]" spin />
          ) : userProfile?.homepage?.isQualifiedCover ? (
            <Icon
              className="absolute right-0 bottom-0 text-[#7b67ee] bg-[#fefdff] text-[32px] rounded-[50%]"
              component={CheckSvg}
            />
          ) : (
            <Icon
              className="absolute right-0 bottom-0 text-[#ff4743] bg-[#fefdff] text-[32px] rounded-[50%]"
              component={FailedSvg}
            />
          )}
        </div>

        <div className="text-center text-[#5d5446] flex items-center flex-col w-full text-[14px] leading-6">
          <div>{t('appearance.uploadTip1')}</div>
          <div>{t('appearance.uploadTip2')}</div>
        </div>

        {userProfile?.homepage?.isQualifiedCover ? null : (
          <div
            className={classNames(
              styles.appearanceInfo,
              {
                [styles.undone]: userProfile?.homepage?.isQualifiedCover === false
              },
              {
                [styles.analyzing]: isLoading
              }
            )}
          >
            <img
              className="w-[34px] h-[20px] shrink-0 object-cover"
              src="https://mindverseglobal.mindverse.com/front-img/crm/1749550519979/voiceCloneMebot.png"
            />
            <div className="text-[14px] mt-3 leading-6 text-primary-new-black w-full">
              {isLoading ? t('appearance.analyzing') : t('appearance.noFaceDetected')}
            </div>
            <div className="text-[12px] mt-1 leading-5 text-[#756C5D] w-full">
              {isLoading ? t('appearance.analyzingDesc') : t('appearance.uploadTip')}
            </div>
          </div>
        )}

        <div
          className="mt-[16px] bg-[#FEFDFF] text-primary-new-black w-fit rounded-[18px] text-center py-[6px] px-4 leading-[24px] text-[14px] cursor-pointer transition-opacity hover:opacity-80"
          onClick={() => {
            setEditOpen(open);
          }}
        >
          <div>{t('homepage.cover.changeCoverImage')}</div>
        </div>
      </div>

      <HomepageEditPageModal handleClose={() => setEditOpen(false)} open={editOpen} />
    </Modal>
  );
};

export default AppearanceModal;
